import * as React from 'react';

import { useImmerReducer } from 'use-immer';

import { PaywallScreens } from '~analytics/types';
import { PaywallDispatch } from '~teacher/components/PaywallDialog';
import { proCheck } from '~teacher/util/proCheck';

interface MoveFolderDialogState {
  isOpen: boolean;
  itemsIdsToMove: Set<string>;
}

type MoveFolderDialogAction =
  | { type: 'open'; selectedQuizItems: Set<string> }
  | { type: 'close' };

const initialState: MoveFolderDialogState = {
  isOpen: false,
  itemsIdsToMove: new Set(),
};

function moveFolderDialogReducer(
  state: MoveFolderDialogState,
  action: MoveFolderDialogAction
) {
  switch (action.type) {
    case 'open': {
      const { selectedQuizItems } = action;
      state.isOpen = true;
      state.itemsIdsToMove = selectedQuizItems;
      return state;
    }
    case 'close': {
      state.isOpen = false;
      state.itemsIdsToMove = new Set();
      return state;
    }
    default:
      return state;
  }
}

export type CloseMoveDialog = (success: boolean, newParentId?: number) => void;
export type OpenMoveDialog = (...selectedQuizItems: string[]) => void;

export const useMoveFolderDialog = (
  selectNone: () => void,
  setParentId: (newParentId: number) => void,
  featurePaywallDispatch: PaywallDispatch
) => {
  const [moveDialogState, dispatch] = useImmerReducer<
    MoveFolderDialogState,
    MoveFolderDialogAction
  >(moveFolderDialogReducer, initialState);

  const closeMoveDialog: CloseMoveDialog = React.useCallback(
    (success: boolean, newParentId?: number) => {
      if (success) {
        setParentId(newParentId);
        selectNone();
      }

      dispatch({ type: 'close' });
    },
    [dispatch, selectNone, setParentId]
  );

  const openMoveDialog: OpenMoveDialog = React.useCallback(
    (...selectedQuizItems: string[]) => {
      if (!proCheck.canMakeFolders()) {
        featurePaywallDispatch({
          type: 'OPEN',
          touch: [PaywallScreens.QuizListMoveItem],
        });
      } else {
        dispatch({
          type: 'open',
          selectedQuizItems: new Set(selectedQuizItems),
        });
      }
    },
    [dispatch, featurePaywallDispatch]
  );

  return {
    moveDialogState: moveDialogState,
    closeMoveDialog: closeMoveDialog,
    openMoveDialog: openMoveDialog,
  };
};
